import ThemeToggle from "../../components/ThemeToggle";
import React, { useState, useEffect } from "react";
import usePageTitle from '../../hooks/usePageTitle';
import { Link, useNavigate } from "react-router-dom";
import { usePartner } from "../../context/PartnerContext";
import { PartnerHero, PartnerSections, PartnerCta, PartnerFooter } from "./PartnerLanding";
import { LANDING_EXAMPLE } from "../partner/landingExamples";

// ── Price Ticker ──────────────────────────────────────────────────
function PriceTicker({ broker }) {
  const PAIRS = ["XAUUSD","EURUSD","GBPUSD","USDJPY","USDCHF","AUDUSD","USDCAD","BTCUSD","NZDUSD","EURGBP"];
  const [prices, setPrices] = React.useState({});
  const [prevPrices, setPrevPrices] = React.useState({});
  const wsRef = React.useRef(null);

  React.useEffect(() => {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/api/ws/price?pairs=${PAIRS.join(",")}`;
    const connect = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const tick = JSON.parse(evt.data);
          if (tick.type !== "tick") return;
          setPrevPrices(p => ({ ...p, [tick.pair]: p[tick.pair] }));
          setPrices(p => {
            setPrevPrices(prev => ({ ...prev, [tick.pair]: p[tick.pair] }));
            return { ...p, [tick.pair]: { bid: tick.bid, ask: tick.ask } };
          });
        } catch {}
      };
      ws.onclose = () => setTimeout(connect, 3000);
      ws.onerror = () => ws.close();
    };
    connect();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, []);

  const fmt = (pair, price) => {
    if (!price) return "—";
    const p = pair.toUpperCase();
    if (p.includes("JPY")) return price.toFixed(3);
    if (p.includes("XAU") || p.includes("XAG")) return price.toFixed(2);
    if (p.includes("BTC") || p.includes("ETH")) return price.toFixed(1);
    return price.toFixed(5);
  };

  return (
    <div style={{
      borderTop: "1px solid var(--border-subtle)",
      borderBottom: "1px solid var(--border-subtle)",
      background: "var(--bg-surface)",
      overflow: "hidden", position: "relative",
      width: "100vw", marginLeft: "calc(-50vw + 50%)",
    }}>
      <div style={{
        display: "flex", alignItems: "center",
        animation: "tickerScroll 40s linear infinite",
        whiteSpace: "nowrap", padding: "10px 0",
      }}>
        {[...PAIRS, ...PAIRS].map((pair, i) => {
          const p = prices[pair];
          const prev = prevPrices[pair];
          const mid = p ? (p.bid + p.ask) / 2 : null;
          const prevMid = prev ? (prev.bid + prev.ask) / 2 : null;
          const up = mid && prevMid ? mid > prevMid : null;
          const color = up === true ? "var(--success)" : up === false ? "var(--danger)" : "var(--text-muted)";
          return (
            <span key={`${pair}-${i}`} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "0 20px", borderRight: "1px solid var(--border-subtle)",
              fontSize: 12,
            }}>
              <span style={{ fontWeight: 700, color: "var(--text-main)", fontSize: 11 }}>{pair}</span>
              <span style={{ fontWeight: 700, color, fontFamily: "monospace", fontSize: 12 }}>
                {mid ? fmt(pair, mid) : "—"}
              </span>
              {up !== null && <span style={{ fontSize: 10, color }}>{up ? "▲" : "▼"}</span>}
            </span>
          );
        })}
      </div>

      <style>{`
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

const gradeColor = g =>
  g?.startsWith("A") ? "var(--success)" :
  g?.startsWith("B") ? "var(--accent)" :
  g?.startsWith("C") ? "var(--warning)" : "var(--danger)";

const riskColor = r => "var(--text-muted)";
const riskBg = r => "var(--bg-elevated)";

const roiColor = v => (v >= 0 ? "var(--success)" : "var(--danger)");
const ddColor = v => (v > 20 ? "var(--danger)" : v > 10 ? "var(--warning)" : "var(--success)");

const selStyle = {
  background: "var(--bg-surface)", border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)", padding: "7px 12px",
  color: "var(--text-main)", fontSize: 12, cursor: "pointer",
};

export default function LandingPage() {
  usePageTitle("Copy Trading & Signals");
  const { appName, logoUrl, tagline, partner } = usePartner();
  const landing = partner ? (partner.landing && Object.keys(partner.landing).length > 0 ? partner.landing : LANDING_EXAMPLE) : {};

  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isLoggedIn = !!(localStorage.getItem("access_token") || localStorage.getItem("token"));
  const userRole = localStorage.getItem("user_role") || "trader";
  const dashboardPath = isLoggedIn ? `/${userRole}` : "/login";

  const [traders, setTraders] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [traderDetail, setTraderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [snapIdx, setSnapIdx] = useState(0);
  const [fees, setFees] = useState({
    trader: { performance_fee_pct: 20, subscription_fee_usd: 0, trader_share_pct: 70 },
    analyst: { performance_fee_pct: 20, subscription_fee_usd: 0, analyst_share_pct: 70 },
  });
  const [brokerPartners, setBrokerPartners] = React.useState([]);
  const [banner, setBanner] = React.useState(null);

  useEffect(() => {
    fetch("/api/public/banner")
      .then(r => r.json())
      .then(d => { if (d.ok && d.banner?.is_active) setBanner(d.banner); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch("/api/public/brokers")
      .then(r => r.json())
      .then(d => {
        const active = (d.data || []).filter(b => b.is_active && b.show_in_ticker).slice(0, 4);
        setBrokerPartners(active);
      }).catch(() => {});
  }, []);

  const [sortTraders, setSortTraders] = useState("alphaScore");
  const [sortAnalysts, setSortAnalysts] = useState("alphaScore");
  const [filterRisk, setFilterRisk] = useState("All");
  const [filterPlatform, setFilterPlatform] = useState("All");

  useEffect(() => {
    fetch("/api/public/fees").then(r => r.json()).then(d => { if (d.trader) setFees(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!traders[selectedIdx]?.id) return;
    setDetailLoading(true);
    fetch(`/api/public/trader/${traders[selectedIdx].id}`)
      .then(r => r.json())
      .then(d => { if (d.ok) setTraderDetail(d); })
      .finally(() => setDetailLoading(false));
  }, [selectedIdx, traders]);

  useEffect(() => {
    Promise.all([
      fetch("/api/public/traders").then(r => r.json()),
      fetch("/api/public/marketplace").then(r => r.json()),
    ]).then(([td, ad]) => {
      if (td.ok) setTraders(td.traders || []);
      if (ad.ok) setAnalysts(ad.sets || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const t = setInterval(() => setSnapIdx(i => i + 1), 5000);
    return () => clearInterval(t);
  }, [loaded]);

  const sortedTraders = [...traders]
    .filter(t => filterRisk === "All" || t.riskLevel === filterRisk)
    .filter(t => filterPlatform === "All" || t.platform === filterPlatform)
    .sort((a, b) => {
      if (sortTraders === "winRate") return b.winRate - a.winRate;
      if (sortTraders === "roi") return b.roi - a.roi;
      if (sortTraders === "maxDD") return a.maxDD - b.maxDD;
      return b.alphaScore - a.alphaScore;
    });

  const sortedAnalysts = [...analysts].sort((a, b) => {
    if (sortAnalysts === "winRate") return b.winRate - a.winRate;
    if (sortAnalysts === "profitFactor") return b.profitFactor - a.profitFactor;
    if (sortAnalysts === "subscribers") return b.subscribers - a.subscribers;
    return b.alphaScore - a.alphaScore;
  });

  // Format umur signal set dari createdAt, konsisten dgn format accountAge trader (Xd/Xmo/Xyr)
  const formatSignalAge = (createdAt) => {
    if (!createdAt) return null;
    const created = new Date(createdAt);
    if (isNaN(created)) return null;
    const days = Math.floor((Date.now() - created.getTime()) / (1000*60*60*24));
    if (days < 30) return `${days}d`;
    if (days < 365) return `${Math.floor(days/30)}mo`;
    return `${Math.floor(days/365)}yr`;
  };

  // Unified all strategies — traders + analysts normalized
  const sortedAll = [
    ...sortedTraders.map(t => ({
      _type: "live_trader",
      _id: t.id,
      name: t.nickname || t.traderName || t.name,
      sub: `${t.riskLevel||"MEDIUM"} Risk · ${t.platform} · ${t.accountAge ? "Trading for "+t.accountAge+" · " : ""}${t.totalTrades} trades`,
      alphaScore: t.alphaScore,
      grade: t.grade,
      riskLevel: t.riskLevel,
      winRate: t.winRate,
      maxDD: t.maxDD,
      roi: t.roi,
      totalAUM: t.totalAUM || 0,
      sourceType: "live_trader",
    })),
    ...sortedAnalysts.map(a => ({
      _type: "analyst",
      _id: a.id,
      name: a.name,
      sub: `${a.riskLevel||"MEDIUM"} Risk · by ${a.analystName} · ${formatSignalAge(a.createdAt) ? "Active "+formatSignalAge(a.createdAt)+" · " : ""}${a.totalSignals} signals`,
      alphaScore: a.virtualAlphaScore || a.alphaScore,
      grade: a.virtualAlphaGrade || a.alphaGrade,
      riskLevel: a.riskLevel || "LOW",
      winRate: a.winRate,
      roi: a.virtualRoiPct || a.cumulativeR,
      maxDD: a.virtualMaxDDPct || 0,
      totalAUM: a.totalAUM || 0,
      subscribers: a.subscribers || 0,
      sourceType: a.sourceType || "manual_analyst",
    })),
].sort((a, b) => {
  if (sortTraders === "winRate") return (b.winRate||0) - (a.winRate||0);
  if (sortTraders === "roi") return (b.roi||0) - (a.roi||0);
  if (sortTraders === "maxDD") return (a.maxDD||0) - (b.maxDD||0);
  return b.alphaScore - a.alphaScore;
});

  const sel = traders[selectedIdx];
  const an0 = analysts[0];

  // Snapshot: alphaScore>40, grade!=F, min 5 trades/signals, top 5
  const snapItems = [
    ...traders.map(t => ({ ...t, _stype: 'trader' })),
    ...analysts.map(a => ({ ...a, _stype: 'analyst' })),
  ].filter(s => {
    const score = s.alphaScore || 0;
    const grade = s.grade || s.alphaGrade || 'F';
    const vol   = (s.totalTrades || 0) + (s.totalSignals || 0);
    return score > 40 && grade !== 'F' && vol >= 5;
  }).sort((a, b) => (b.alphaScore || 0) - (a.alphaScore || 0)).slice(0, 5);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text-main)", overflowX: "hidden" }}>

      <style>{`
        .hero-grid { display: grid; grid-template-columns: minmax(0,1.6fr) minmax(0,1fr); gap: 20px; align-items: start; }
        .nav-links { display: flex; align-items: center; gap: 20px; }
        .nav-mobile-btn { display: none; }
        .mobile-menu { display: none; flex-direction: column; padding: 12px 24px 16px; border-top: 1px solid var(--border-subtle); gap: 12px; }
        .mobile-menu.open { display: flex; }
        .steps-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; }
        .earn-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; }
        .footer-inner { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px; align-items: center; }
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-snapshots { display: none !important; }
          .hero-badges { display: none !important; }
          .nav-links { display: none !important; }
          .nav-mobile-btn { display: flex !important; align-items: center; }
          .steps-grid { grid-template-columns: 1fr !important; }
          .earn-grid { grid-template-columns: 1fr !important; }
          .leaderboard-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; padding: 12px 14px !important; }
          .leaderboard-row-right { flex-direction: row !important; flex-wrap: wrap !important; gap: 10px !important; width: 100% !important; }
          .leaderboard-rank { display: none !important; }
          .problem-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
          .solution-grid { grid-template-columns: 1fr !important; }
          .cta-box { padding: 32px 20px !important; }
          .leaderboard-filters { flex-direction: column !important; align-items: flex-start !important; }
          .leaderboard-filter-selects { flex-wrap: wrap !important; gap: 6px !important; }
          .alphascore-badge { display: none !important; }
          .problem-box { padding: 20px 18px !important; }
          .leaderboard-section { padding: 32px 16px 0 !important; }
          .footer-inner { flex-direction: column; align-items: flex-start; gap: 14px; }
        }
      `}</style>

      {/* BANNER BAR */}
      {banner && (
        <div style={{
          background: "var(--accent)", color: "#fff",
          padding: "9px 24px", textAlign: "center",
          fontSize: 13, display: "flex", alignItems: "center",
          justifyContent: "center", gap: 12, position: "relative",
          zIndex: 60,
        }}>
          <span style={{ fontWeight: 500 }}>{banner.message}</span>
          <a href={banner.cta_link}
            style={{ padding: "4px 14px", borderRadius: "var(--radius-md)",
              background: "#fff", color: "var(--accent)",
              fontWeight: 700, fontSize: 12, textDecoration: "none", flexShrink: 0 }}>
            {banner.cta_text} →
          </a>
          <button onClick={() => setBanner(null)}
            style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", color: "rgba(255,255,255,0.7)",
              cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 4 }}></button>
        </div>
      )}

      {/* NAV */}
      <header style={{ position: "sticky", top: 0, zIndex: 50,
        background: "var(--bg-surface)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px", height: 58,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ textDecoration: "none", flexShrink: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "var(--text-main)", letterSpacing: "-0.02em" }}>
              {logoUrl && <img src={logoUrl} alt={appName} style={{ height: 26, marginRight: 8, verticalAlign: "middle", objectFit: "contain" }} onError={e => e.target.style.display = "none"} />}{appName === "CrunchAlpha" ? (<>Crunch<span style={{ color: "var(--accent)" }}>Alpha</span></>) : appName}
            </div>
            {tagline && <div style={{ fontSize: 10, color: "var(--accent)", marginTop: 1, fontWeight: 600 }}>{tagline}</div>}
          </Link>
          <div className="nav-links" style={{ alignItems: "center" }}>
            <a href="#leaderboard" style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "none" }}>Leaderboard</a>
            {(!partner || landing["how.title"]) && <a href="#how" style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "none" }}>How it works</a>}
            {!partner && <a href="/brokers" style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "none" }}>Brokers</a>}
            {!partner && <a href="/qa" style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "none" }}>FAQ</a>}
            {isLoggedIn ? (
              <>
                <Link to={dashboardPath} style={{ padding: "7px 18px", borderRadius: "var(--radius-md)",
                  background: "var(--accent)", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                  Dashboard
                </Link>
                <span onClick={() => { localStorage.clear(); window.location.href="/login"; }}
                  style={{ fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}>
                  Sign out
                </span>
              </>
            ) : (
              <>
                <Link to="/login" style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "none" }}>Sign in</Link>
                <Link to="/register" style={{ padding: "7px 18px", borderRadius: "var(--radius-md)",
                  background: "var(--accent)", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                  Get started
                </Link>
              </>
            )}
          </div>
          <ThemeToggle />
          <button className="nav-mobile-btn"
            onClick={() => setMobileMenuOpen(o => !o)}
            style={{ background: "none", border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
              color: "var(--text-muted)", padding: "6px 10px", cursor: "pointer", fontSize: 18 }}>
            {mobileMenuOpen ? "" : ""}
          </button>
        </div>
        <div className={`mobile-menu${mobileMenuOpen ? " open" : ""}`}>
          <a href="#leaderboard" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 14, color: "var(--text-muted)", textDecoration: "none" }}>Leaderboard</a>
          {(!partner || landing["how.title"]) && <a href="#how" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 14, color: "var(--text-muted)", textDecoration: "none" }}>How it works</a>}
          {!partner && <a href="/brokers" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 14, color: "var(--text-muted)", textDecoration: "none" }}>Brokers</a>}
          {!partner && <a href="/qa" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 14, color: "var(--text-muted)", textDecoration: "none" }}>FAQ</a>}
          {isLoggedIn ? (
            <>
              <Link to={dashboardPath} style={{ fontSize: 14, color: "var(--accent)", fontWeight: 600, textDecoration: "none" }}>Dashboard</Link>
              <span onClick={() => { localStorage.clear(); window.location.href="/login"; }}
                style={{ fontSize: 14, color: "var(--text-muted)", cursor: "pointer" }}>Sign out</span>
            </>
          ) : (
            <>
              <Link to="/login" style={{ fontSize: 14, color: "var(--text-muted)", textDecoration: "none" }}>Sign in</Link>
              <Link to="/register" style={{ fontSize: 14, color: "var(--accent)", fontWeight: 600, textDecoration: "none" }}>Get started</Link>
            </>
          )}
        </div>
      </header>

      {/* PRICE TICKER */}
      <PriceTicker broker={partner ? null : (brokerPartners[0] || null)} />

      {/* FEATURED BROKERS BAR */}
      {!partner && brokerPartners.length > 0 && (
        <div style={{ background: "var(--bg-surface)", borderBottom: "1px solid var(--border-subtle)", padding: "0 24px" }}>
          <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", gap: 0 }}>
            <span style={{ fontSize: 10, color: "var(--accent)", fontWeight: 700, letterSpacing: "0.12em",
              textTransform: "uppercase", paddingRight: 16, borderRight: "1px solid var(--border-subtle)",
              whiteSpace: "nowrap", flexShrink: 0 }}>
              Featured Brokers
            </span>
            {brokerPartners.map((b, i) => (
              <a key={b.id || i}
                href={b.referral_link || b.website_url || "#"}
                target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
                  borderRight: "1px solid var(--border-subtle)", textDecoration: "none", flexShrink: 0, flex: 1 }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-elevated)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {b.logo_url
                  ? <img src={b.logo_url} alt={b.name} style={{ height: 26, width: 26, objectFit: "contain", borderRadius: 4, flexShrink: 0 }} />
                  : <div style={{ width: 26, height: 26, borderRadius: 4, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, fontWeight: 800, color: "#fff", flexShrink: 0 }}>{b.name[0]}</div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-main)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</div>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", whiteSpace: "nowrap" }}>
                    {[b.regulation, b.rebate_per_lot > 0 ? `$${b.rebate_per_lot}/lot rebate` : null].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div style={{ padding: "4px 10px", background: "var(--accent)", color: "#fff",
                  borderRadius: "var(--radius-md)", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                  Open Account
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* HERO */}
      <section style={{ maxWidth: 1080, margin: "0 auto", padding: "40px 24px 24px" }}>
        <div className="hero-grid">
          {partner ? (
            <PartnerHero landing={landing} appName={appName} partner={partner} isLoggedIn={isLoggedIn} dashboardPath={dashboardPath} navigate={navigate} />
          ) : (
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
              background: "var(--accent-soft)", border: "1px solid var(--accent-border)",
              borderRadius: "var(--radius-full)", padding: "4px 14px",
              fontSize: 11, color: "var(--accent)", marginBottom: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text-muted)", display: "inline-block" }} />
               Non-custodial  ·   Verified Performance Data  ·   Risk-Normalized
            </div>
            <h1 style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.1,  margin: "0 0 14px", letterSpacing: "-0.03em" }}>
              Copy Trade Strategies with<br />
              <span style={{ color: "var(--accent)" }}>Verified Risk Data — Not Promises.</span>
            </h1>
                        <p style={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 420, lineHeight: 1.75, margin: "0 0 6px" }}>
              Real performance data pulled directly from your broker account.
              Risk-normalized position sizing — you never over-allocate.
              Your funds stay in your own broker. Always.
            </p>
            <p style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600, fontStyle: "italic", margin: "0 0 12px" }}>
              We don’t publish trades. We publish plans.
            </p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10,
              color: "var(--text-faint)", background: "var(--bg-surface)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-full)",
              padding: "3px 10px", marginBottom: 16 }}>
              Currently: Forex via MT4/MT5  ·  Stocks & more coming soon
            </div>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 22 }}>
              {[{ t: "Non-custodial" }, { t: "Real drawdown data" }, { t: "Auto lot sizing" }].map(ck => (
                <span key={ck.t} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-muted)" }}>
                  <span style={{ color: "var(--success)", fontWeight: 800, fontSize: 14 }}></span> {ck.t}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              <button onClick={() => navigate(isLoggedIn ? dashboardPath : "/register?role=investor")}
                style={{ padding: "11px 24px", borderRadius: "var(--radius-md)", background: "var(--accent)",
                  color: "#fff", fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer" }}>
                Start as Investor
              </button>
              <button onClick={() => navigate(isLoggedIn ? dashboardPath : "/register")}
                style={{ padding: "10px 18px", borderRadius: "var(--radius-md)",
                  background: "transparent", border: "1px solid var(--border)",
                  color: "var(--text-muted)", fontSize: 13, cursor: "pointer" }}>
                I'm a Trader / Analyst
              </button>
            </div>
            <p style={{ fontSize: 12, color: "var(--text-faint)", marginBottom: 16 }}>
              {fees.trader?.subscription_fee_usd > 0 ? `From $${fees.trader.subscription_fee_usd}/mo · ` : ""}
              No subscription for auto-copy — performance fee only when you profit
            </p>

          </div>
          )}

          {/* Rotating Snapshot */}
          <div className="hero-snapshots" style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-xl)", padding: 20, border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--success)", display: "inline-block", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 11, color: "var(--text-faint)" }}>Live · Top Strategy Snapshot</span>
              </div>
              {snapItems.length > 1 && (
                <div style={{ display: "flex", gap: 4 }}>
                  {snapItems.map((_, i) => (
                    <span key={i} onClick={() => setSnapIdx(i)}
                      style={{ width: 6, height: 6, borderRadius: "50%", cursor: "pointer", display: "inline-block",
                        background: (snapIdx % snapItems.length) === i ? "var(--accent)" : "var(--border)",
                        transition: "background 0.2s" }} />
                  ))}
                </div>
              )}
            </div>
            {(() => {
              if (!loaded || snapItems.length === 0) return (
                <div style={{ fontSize: 11, color: "var(--text-faint)", textAlign: "center", padding: "40px 0" }}>Loading...</div>
              );
              const s   = snapItems[snapIdx % snapItems.length];
              const isT = s._stype === 'trader';
              const gr  = s.grade || s.alphaGrade || '—';
              const gc  = gradeColor(gr);
              const wr  = s.winRate || 0;
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{isT ? (s.nickname || s.traderName) : s.name}</div>
                        {isT
                          ? <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 10, background: "var(--success-soft)", color: "var(--success)", border: "1px solid rgba(34,197,94,0.25)" }}>   Live Trader</span>
                          : <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 10, background: "var(--bg-elevated)", color: "var(--text-muted)", border: "1px solid var(--border-subtle)" }}>   Analyst</span>
                        }
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text-faint)" }}>
                        {isT ? `${s.riskLevel||"MEDIUM"} Risk · ${s.platform} · ${s.totalTrades} trades` : `${s.totalSignals} signals · ${s.subscribers || 0} followers`}
                      </div>
                    </div>
                    <div style={{ textAlign: "center", flexShrink: 0, marginLeft: 8 }}>
                      <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 2 }}>AlphaScore</div>
                      <div style={{ padding: "3px 10px", borderRadius: "var(--radius-md)", fontSize: 12, fontWeight: 700,
                        background: gc + "1a", color: gc }}>
                        {s.alphaScore?.toFixed(1)} {gr}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 5, marginBottom: 2 }}>Risk Level</div>
                      <div style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: "var(--radius-sm)",
                        background: "var(--bg-elevated)", color: "var(--text-muted)" }}>
                        {s.riskLevel || '—'}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--text-faint)", marginBottom: 3 }}>
                      <span>Win Rate</span>
                      <span style={{ fontWeight: 700, color: "var(--text-main)" }}>{wr.toFixed(1)}%</span>
                    </div>
                    <div style={{ height: 4, background: "var(--bg-elevated)", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${wr}%`, background: "var(--accent)", borderRadius: 999, transition: "width 0.4s" }} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12 }}>
                    {(isT ? [
                      { l: "ROI",    v: (s.roi >= 0 ? "+" : "") + (s.roi || 0).toFixed(1) + "%", color: roiColor(s.roi || 0) },
                      { l: "Max DD", v: (s.maxDD || 0).toFixed(1) + "%", color: ddColor(s.maxDD || 0) },
                      { l: "Trades", v: String(s.totalTrades) },
                      { l: "Broker", v: s.broker || "—" },
                    ] : [
                      { l: "Avg RR",    v: (s.cumulativeR || s.roi || 0).toFixed(2) + "R" },
                      { l: "Signals",   v: String(s.totalSignals) },
                      { l: "Followers", v: String(s.subscribers || 0) },
                      { l: "Analyst",   v: s.analystName || "—" },
                    ]).map(m => (
                      <div key={m.l} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "7px 10px" }}>
                        <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 2 }}>{m.l}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: m.color || "var(--text-main)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.v}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => navigate(isLoggedIn ? dashboardPath : "/register")}
                    style={{ width: "100%", padding: "9px", borderRadius: "var(--radius-md)", border: "none",
                      cursor: "pointer", background: "var(--accent)", color: "#fff", fontSize: 12, fontWeight: 600 }}>
                    Register to view full profile
                  </button>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", textAlign: "center", marginTop: 8 }}>
                    Showing: AlphaScore &gt; 40 · Grade B+ · Min 5 trades/signals
                  </div>
                </>
              );
            })()}
          </div>

        </div>
      </section>

      {/* STATS BAR */}
      {loaded && (traders.length + analysts.length > 0) && (
        <section style={{ maxWidth: 1080, margin: "16px auto 0", padding: "0 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
            borderRadius: "var(--radius-xl)", border: "1px solid var(--border)", overflow: "hidden",
            background: "var(--border)", gap: 1 }}>
            {[
              { v: traders.length,  label: "Active Traders",   col: "var(--success)"   },
              { v: analysts.length, label: "Signal Providers", col: "var(--accent)"    },
              { v: "100%",          label: "Non-Custodial",    col: "var(--success)"   },
              { v: "Real",          label: "Verified Data",    col: "var(--text-main)" },
            ].filter(row => !partner || typeof row.v === "number").map(row => (
              <div key={row.label} style={{ background: "var(--bg-surface)", padding: "16px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: row.col, letterSpacing: "-0.02em", lineHeight: 1 }}>{row.v}</div>
                <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 5 }}>{row.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {partner ? (<PartnerSections landing={landing} />) : (<>
      {/* PROBLEM */}
      <section style={{ maxWidth: 1080, margin: "48px auto 0", padding: "0 24px" }}>
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", padding: "32px 36px" }} className="problem-box">
          <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>The Problem With Copy Trading</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: "var(--text-main)" }}>Most Platforms Show You ROI. We Show You Risk.</div>
          <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 16 }}>
            Profitable trader ≠ safe investment. Most platforms show P&amp;L screenshots — not real drawdown, not over-leverage, not position sizing risk.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
            {[
              { bad: "Traders over-leverage with no limits",          fix: "Risk-normalized: your position auto-adjusted to your capital" },
              { bad: "You take more risk than you expect",             fix: "25+ risk flags detect unstable behavior automatically"        },
              { bad: "No systematic way to evaluate discipline",       fix: "AlphaRank scores consistency + discipline, not just P&L"      },
              { bad: "Profitable trader ≠ safe for your capital", fix: "Real drawdown from broker — no screenshots, no hidden losses" },
            ].map(item => (
              <div key={item.bad} style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", gap: 8, padding: "10px 14px", background: "var(--danger-soft)", borderBottom: "1px solid var(--border)", alignItems: "flex-start" }}>
                  <span style={{ color: "var(--danger)", fontWeight: 800, fontSize: 14, flexShrink: 0, lineHeight: 1.4 }}></span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>{item.bad}</span>
                </div>
                <div style={{ display: "flex", gap: 8, padding: "10px 14px", background: "var(--success-soft)", alignItems: "flex-start" }}>
                  <span style={{ color: "var(--success)", fontWeight: 800, fontSize: 14, flexShrink: 0, lineHeight: 1.4 }}></span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>{item.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section style={{ maxWidth: 1080, margin: "20px auto 0", padding: "0 24px" }}>
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", padding: "32px 36px" }} className="problem-box">
          <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Our Solution</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: "var(--text-main)" }}>A Risk-Controlled Copy Trading System</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 20 }}>
            {[
              { title: "Risk-Normalized Execution", text: "Trade size is automatically adjusted based on your capital and allocation — not the trader's lot size." },
              { title: "Risk Flag Detection",        text: "Over-leverage, no stop loss, and unstable behavior are detected and flagged automatically." },
              { title: "AlphaRank Scoring",          text: "Traders and analysts ranked by performance, discipline, and consistency — not hype." },
              { title: "Transparent Data",           text: "Real trade history, real drawdown, no hidden metrics." },
            ].map(s => (
              <div key={s.title}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text-muted)", display: "inline-block", marginBottom: 10 }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-main)", marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7 }}>{s.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" style={{ maxWidth: 1080, margin: "48px auto 0", padding: "0 24px" }}>
        <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>How It Works</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>3 simple steps</div>
        <div className="steps-grid" style={{ marginBottom: 40 }}>
          {[
            { step: "1", title: "Choose Providers",    text: "Follow traders or analysts based on AlphaRank, risk level, and track record." },
            { step: "2", title: "Set Your Allocation", text: "Decide how much capital to allocate. The system handles position sizing automatically." },
            { step: "3", title: "Copy Automatically",  text: "Trades and signals sync automatically by default — with risk controlled in the background. Prefer manual? You can follow signals that way too." },
          ].map(s => (
            <div key={s.step} style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-xl)", padding: 20, border: "1px solid var(--border)" }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--accent-soft)",
                border: "1px solid var(--accent-border)", display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 14, fontWeight: 700, color: "var(--accent)", marginBottom: 14 }}>
                {s.step}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: "var(--text-main)" }}>{s.title}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7 }}>{s.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* THREE ROLES */}
      <section style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Platform</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>One platform. Three roles.</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 28, maxWidth: 500, lineHeight: 1.7 }}>
          Investor, Trader, or Analyst — the system aligns risk, performance, and incentives for every participant.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>

          <div style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-xl)", padding: 20, border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--accent)", marginBottom: 4 }}>For Investors</div>
            <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 16, lineHeight: 1.5 }}>Back strategies with controlled risk</div>
            <ul style={{ fontSize: 12, color: "var(--text-muted)", paddingLeft: 16, lineHeight: 2, margin: 0 }}>
              <li>1 broker account — copy multiple strategies simultaneously</li>
              <li>From $9.99/mo · performance fee only when you profit</li>
              <li>{fees.trader?.performance_fee_pct || 25}% performance fee — only when you profit</li>
              <li>Funds stay in your own broker — fully non-custodial</li>
              <li>Choose: copy live trades or subscribe to verified signal plans</li>
            </ul>
          </div>

          <div style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-xl)", padding: 20, border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--accent)", marginBottom: 4 }}>For Traders</div>
            <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 16, lineHeight: 1.5 }}>Your track record becomes your product</div>
            <ul style={{ fontSize: 12, color: "var(--text-muted)", paddingLeft: 16, lineHeight: 2, margin: "0 0 14px" }}>
              <li>Connect your MT4/MT5 — performance syncs automatically</li>
              <li>Keep {fees.trader?.trader_share_pct || 80}% of all performance fees</li>
              <li>Get paid based on results, not volume</li>
              <li>Build reputation through AlphaRank™</li>
            </ul>
            <div style={{ background: "var(--accent-soft)", border: "1px solid rgba(78,154,241,0.25)", borderRadius: "var(--radius-lg)", padding: "12px 14px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)", marginBottom: 6 }}>   SignalSync™ — optional add-on</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 6 }}>
                Activate to turn your trades into verified signal plans. Every position with SL &amp; TP becomes a plan investors can follow — your thesis on record, regardless of execution.
              </div>
              <div style={{ fontSize: 11, color: "var(--accent)", fontStyle: "italic" }}>
                A second execution path for investors — plan vs live trade, side by side.
              </div>
            </div>
          </div>

          <div style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-xl)", padding: 20, border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--accent)", marginBottom: 4 }}>For Analysts</div>
            <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 16, lineHeight: 1.5 }}>Publish signal plans — no live account needed</div>
            <ul style={{ fontSize: 12, color: "var(--text-muted)", paddingLeft: 16, lineHeight: 2, margin: 0 }}>
              <li>No live trading account required</li>
              <li>Followers can auto-copy your signals or follow manually via subscription</li>
              <li>Keep {fees.analyst?.analyst_share_pct || 80}% of performance fees from auto-copy, plus subscription revenue from manual followers</li>
              <li>Build a verified signal track record</li>
              <li>Upgrade to SignalSync™ anytime by connecting a live account</li>
            </ul>
          </div>

        </div>
      </section>

      </>)}

      {/* LEADERBOARD */}
      <section id="leaderboard" className="leaderboard-section" style={{ maxWidth: 1080, margin: "0 auto", padding: "48px 24px 0" }}>
        <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Leaderboard</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Top Strategies Right Now</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20 }}>Ranked by AlphaRank™ — real performance, not marketing claims.</div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <select value={sortTraders} onChange={e => setSortTraders(e.target.value)} style={selStyle}>
            <option value="alphaScore">Sort: AlphaScore</option>
            <option value="winRate">Sort: Win Rate</option>
            <option value="roi">Sort: ROI</option>
            <option value="maxDD">Sort: Low DD</option>
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
            {(!loaded || sortedAll.length === 0) ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--text-faint)", fontSize: 13 }}>{loaded ? "No strategies yet." : "Loading..."}</div>
            ) : sortedAll.map((s, i) => {
              const badge = s.sourceType === "signal_sync"
                ? { label: "   SignalSync™", color: "var(--accent)", bg: "var(--accent-soft)", border: "rgba(78,154,241,0.25)" }
                : s.sourceType === "live_trader"
                ? { label: "   Live Trader", color: "var(--success)", bg: "var(--success-soft)", border: "rgba(34,197,94,0.25)" }
                : { label: "   Analyst", color: "var(--text-muted)", bg: "var(--bg-elevated)", border: "var(--border-subtle)" };
              return (
                <div key={s._id}
                  onClick={() => navigate(isLoggedIn ? (s._type === "live_trader" ? `/marketplace/trader/${s._id}` : `/marketplace/analyst/${s._id}`) : "/register")}
                  className="leaderboard-row"
                  style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-lg)", padding: "14px 18px",
                    border: "1px solid var(--border)", display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 16, cursor: "pointer", transition: "border-color 0.15s", width: "100%" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--bg-elevated)",
                      border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center",
                      justifyContent: "center", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", flexShrink: 0 }}>
                      {i + 1}
                    </div>
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                        <span style={{ fontSize:9, fontWeight:800, padding:"1px 7px", borderRadius:10,
                          background: badge.bg, color: badge.color, border:`1px solid ${badge.border}`,
                        }}>{badge.label}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 2 }}>{s.sub}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0, flexWrap: "wrap" }} className="leaderboard-row-right">
                    {[
                      { l: "Win Rate", v: (s.winRate||0).toFixed(1) + "%" },
                      { l: "Max DD", v: (s.maxDD||0).toFixed(1)+"%", color: ddColor(s.maxDD||0) },
                      { l: "ROI", v: (s.roi>=0?"+":"")+s.roi?.toFixed(1)+"%", color: roiColor(s.roi||0) },
                      { l: "AUM", v: s.totalAUM>0?("$"+(s.totalAUM>=1000000?(s.totalAUM/1000000).toFixed(1)+"M":s.totalAUM>=1000?(s.totalAUM/1000).toFixed(1)+"K":s.totalAUM.toFixed(0))):"—" },
                    ].map(m => (
                      <div key={m.l} style={{ textAlign: "center", minWidth: 56 }}>
                        <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 2 }}>{m.l}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: m.color || "var(--text-main)" }}>{m.v}</div>
                      </div>
                    ))}
                    <div style={{ textAlign: "center", minWidth: 80 }}>
                      <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 4 }}>AlphaScore</div>
                      <div style={{ padding: "3px 10px", borderRadius: "var(--radius-md)", fontSize: 12, fontWeight: 700,
                        background: gradeColor(s.grade) + "1a", color: gradeColor(s.grade), border: `1px solid ${gradeColor(s.grade)}33` }}>
                        {s.alphaScore?.toFixed(1)} · {s.grade}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {tab === "analysts_hidden" && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <select value={sortAnalysts} onChange={e => setSortAnalysts(e.target.value)} style={selStyle}>
                <option value="alphaScore">Sort: AlphaScore</option>
                <option value="winRate">Sort: Win Rate</option>
                <option value="profitFactor">Sort: Profit Factor</option>
                <option value="avgRR">Sort: Avg RR</option>
                <option value="subscribers">Sort: Followers</option>
              </select>
            </div>
          )}


      </section>

      {/* CTA */}
      {partner ? (<PartnerCta landing={landing} appName={appName} partner={partner} isLoggedIn={isLoggedIn} dashboardPath={dashboardPath} />) : (
      <section style={{ maxWidth: 1080, margin: "48px auto 0", padding: "0 24px 64px" }}>
        <div style={{ background: "var(--accent-soft)", border: "1px solid var(--accent-border)", borderRadius: "var(--radius-xl)", padding: "48px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 12, color: "var(--text-main)", letterSpacing: "-0.02em" }}>
            Start Copy Trading with Real Risk Control
          </div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 28, maxWidth: 400, margin: "0 auto 28px", lineHeight: 1.7 }}>
            Real performance data. Risk-normalized execution. Funds in your own broker — always. Performance fee only when you profit.
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/register?role=investor" style={{ padding: "12px 28px", borderRadius: "var(--radius-md)",
              background: "var(--accent)", color: "#fff", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
              Start as Investor
            </Link>
            <Link to={isLoggedIn ? dashboardPath : "/register"} style={{ padding: "11px 22px", borderRadius: "var(--radius-md)",
              background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)", fontSize: 13, textDecoration: "none" }}>
              Activate SignalSync™ →
            </Link>
          </div>
        </div>
      </section>

      )}

      {/* FOOTER */}
      {partner ? (<PartnerFooter appName={appName} footerText={partner.footerText} landing={landing} />) : (
      <footer style={{ borderTop: "1px solid var(--border-subtle)", padding: "24px", maxWidth: 1080, margin: "0 auto", fontSize: 11, color: "var(--text-faint)" }}>
        <div className="footer-inner">
          <div>
            <span style={{ fontWeight: 700, color: "var(--text-main)" }}>Crunch<span style={{ color: "var(--accent)" }}>Alpha</span></span>
            {" "}© 2026 · Non-custodial · Multi-broker · Trading involves risk.
          </div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <Link to="/about" style={{ color: "var(--text-faint)", textDecoration: "none" }}>About Us</Link>
            <Link to="/legal/terms" style={{ color: "var(--text-faint)", textDecoration: "none" }}>Terms</Link>
            <Link to="/legal/privacy" style={{ color: "var(--text-faint)", textDecoration: "none" }}>Privacy</Link>
            <Link to="/legal/risk" style={{ color: "var(--text-faint)", textDecoration: "none" }}>Risk Disclosure</Link>
          </div>
        </div>
      </footer>
      )}

    </div>
  );
}
